import React, {FC, useState} from "react"

import {FUNDING, PayPalButtons, PayPalButtonsComponentProps, PayPalScriptProvider} from "@paypal/react-paypal-js"
import {PaymentContextSource, PaymentCurrencyType, PaymentMethodConfigs} from "@irati/types"
import {useCreatePaymentContextMutation, useRequestPaymentMutation} from "@irati/payments/hooks"
import {hangPromise} from "@irati/utils/promise"
import Loading from "@irati/ui/src/Loading"

interface Props {
  amount: number
  paymentOrderId: string
  currency: PaymentCurrencyType
  paymentMethodConfigs: PaymentMethodConfigs
  load?: Promise<any>
  setLoad: (value: any) => void
  returnUrl: string
}

const PayPal: FC<Props> = ({paymentMethodConfigs, currency, load, setLoad, amount, returnUrl, paymentOrderId}) => {
  const [paymentContextId, setPaymentContextId] = useState<string>()
  const requestPaymentParams = {
    variables: {
      input: {
        paymentOrderId,
        amount,
        currency,
        details: {
          paymentContextId
        }
      }
    }
  }
  const paymentContextArgs = {
    variables: {
      input: {
        amount,
        currency,
        paymentOrderId,
        source: PaymentContextSource.Paypal
      }
    }
  }
  const [requestPayment] = useRequestPaymentMutation(requestPaymentParams)
  const [createPaymentContext] = useCreatePaymentContextMutation(paymentContextArgs)

  const handleCreateOrder = async () => {
    const req = createPaymentContext()
    setLoad(req)
    const paymentContext = await req
    setLoad(hangPromise())
    setPaymentContextId(paymentContext.uuid)
    return paymentContext.orderId!
  }
  const handleOnApprove = async () => {
    const req = requestPayment()
    setLoad(req)
    await req
    window.location.href = returnUrl
  }
  const handleCancel = () => {
    // User has dismissed the Paypal flow, reset
    setLoad(undefined)
  }
  const handleOnError = () => {
    // We didn't create a payment yet, no charge has been triggered, we can let the user retry
    setLoad(undefined)
  }

  const payPalOptions = {
    clientId: paymentMethodConfigs.extId!,
    currency,
    intent: "capture"
  }

  return (
    <>
      <PayPalScriptProvider options={payPalOptions}>
        <PayPalButtons
          disabled={!!load}
          createOrder={handleCreateOrder}
          onApprove={handleOnApprove}
          onCancel={handleCancel}
          onError={handleOnError}
          style={payPalStyle}
          fundingSource={FUNDING.PAYPAL}
        />
      </PayPalScriptProvider>
      <Loading load={load} useLoader={false} />
    </>
  )
}

export default PayPal

// Update .stylelintignore if moving the style below or lint:css will fail
const payPalStyle: PayPalButtonsComponentProps["style"] = {
  layout: "vertical",
  color: "gold",
  shape: "rect",
  label: "paypal"
}
